import { Controller, Control, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { LuBuilding2, LuHome, LuBriefcase, LuBedDouble, LuMapPin } from "react-icons/lu";
import { BsTelephone } from "react-icons/bs";
import { useMemo } from "react";
import Select from "../../../../../components/Select/Select";
import RestrictedInputField from "../../../../../components/CredentialsInput/RestrictedInputField";
import GoogleApiRestrictedInputField from "../../../../../components/CredentialsInput/GoogleApiRestrictedInputField";
import * as translator from "../../../../../utils/translator";
import { validatePhoneDE } from "../../../../../utils/fieldsValidator";
import type { NewAddressFormType } from "./types";

const GERMANY_COUNTRY_ID = "DE";

type Props = {
  control: Control<NewAddressFormType>;
  watch: UseFormWatch<NewAddressFormType>;
  setValue: UseFormSetValue<NewAddressFormType>;
  language: string | undefined;
  isMobile: boolean;
  isEditMode: boolean;
  isGoogleAPILoaded: boolean;
  cartCountryID: string;
  countryCurrenciesByID: Record<string, any>;
  onOpenMap: () => void;
};

const AddressFormFields: React.FC<Props> = ({
  control,
  watch,
  setValue,
  language,
  isMobile,
  isEditMode,
  isGoogleAPILoaded,
  cartCountryID,
  countryCurrenciesByID,
  onOpenMap,
}) => {
  const locationTypeOptions = useMemo(() => ([
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_apartment") || "Apartment", code: "apartment", icon: <LuBuilding2 size={14} /> },
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_house") || "House", code: "house", icon: <LuHome size={14} /> },
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_work") || "Office", code: "office", icon: <LuBriefcase size={14} /> },
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_hotel") || "Hotel", code: "hotel", icon: <LuBedDouble size={14} /> },
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_other") || "Other", code: "other", icon: <LuMapPin size={14} /> },
  ]), [language]);

  const countryDropdownOptions = useMemo(() => {
    return Object.values(countryCurrenciesByID).map((currency: any) => ({
      value: translator.translate(language, "shipping", `ShippingForm/country_name/${currency.countryID?.toLowerCase()}`),
      code: currency.countryID,
    }));
  }, [countryCurrenciesByID, language]);

  return (
    <div className="overflow-y-auto overflow-x-hidden flex-1 px-3 py-5">

      {/* Map button — only in create mode */}
      {!isEditMode && (
        <>
          <button
            onClick={onOpenMap}
            className="flex items-center justify-center w-full h-14 border border-[#0a9281] rounded-md bg-[#e7fcf7] text-[#616161] text-sm font-semibold font-poppins cursor-pointer mb-1"
          >
            {translator.translate(language, "shipping", "AddNewAddressModal/set_location_on_map") || "Set location on a map"}
          </button>

          {/* OR divider */}
          <div className="flex items-center gap-2 h-[39px] mb-1">
            <div className="flex-1 h-px bg-[#dbe1eb]" />
            <span className="text-[11px] text-[#98a0af] font-poppins">
              {translator.translate(language, "shipping", "AddNewAddressModal/or") || "OR"}
            </span>
            <div className="flex-1 h-px bg-[#dbe1eb]" />
          </div>
        </>
      )}

      {/* Form fields */}
      <div className="flex flex-col gap-3">

        {/* Location Type */}
        <Controller
          name="address_sub_type"
          control={control}
          render={({ field }) => (
            <Select
              id="add-address-form-address-sub-type"
              initialValue={locationTypeOptions.find(o => o.code === field.value) || locationTypeOptions[0]}
              options={locationTypeOptions as any}
              onChangeCallback={({ option }: any) => field.onChange(option.code)}
              questionnaireValidationObject={{}}
              errorMode={false}
              style={{
                border: "1px solid var(--sub-gray)",
                height: "56px",
                width: "100%",
                borderRadius: "6px",
                margin: "0px",
                backgroundColor: "transparent",
              }}
              label={translator.translate(language, "shipping", "ShippingForm/address_sub_type_label") || "Type location"}
            />
          )}
        />

        {/* First Name + Last Name */}
        <div className={`flex ${isMobile ? "flex-col gap-2" : "flex-row gap-3"}`}>
          <div className="flex-1">
            <Controller
              name="first_name"
              control={control}
              render={({ field, fieldState }) => (
                <RestrictedInputField
                  id="add-address-form-first-name"
                  ignoreTouched={true}
                  value={field.value}
                  onChange={(value: string) => field.onChange(value)}
                  placeholder={translator.translate(language, "shipping", "ShippingForm/first_name")}
                  mandatory={true}
                  language={language}
                  onBlur={field.onBlur}
                  errorMsg={fieldState?.error?.message}
                />
              )}
            />
          </div>
          <div className="flex-1">
            <Controller
              name="last_name"
              control={control}
              render={({ field, fieldState }) => (
                <RestrictedInputField
                  id="add-address-form-last-name"
                  ignoreTouched={true}
                  value={field.value}
                  onChange={(value: string) => field.onChange(value)}
                  placeholder={translator.translate(language, "shipping", "ShippingForm/last_name")}
                  mandatory={true}
                  language={language}
                  onBlur={field.onBlur}
                  errorMsg={fieldState?.error?.message}
                />
              )}
            />
          </div>
        </div>

        {/* Zip + City */}
        <div className={`flex ${isMobile ? "flex-col gap-2" : "flex-row gap-3"}`}>
          <div className="flex-1">
            <Controller
              name="zip"
              control={control}
              render={({ field, fieldState }) => (
                <RestrictedInputField
                  id="add-address-form-zip"
                  ignoreTouched={true}
                  value={field.value}
                  onChange={(value: string) => field.onChange(value)}
                  placeholder={translator.translate(language, "shipping", "ShippingForm/zip_code") || "Zip code"}
                  mandatory={true}
                  language={language}
                  onBlur={field.onBlur}
                  errorMsg={fieldState?.error?.message}
                />
              )}
            />
          </div>
          <div className="flex-1">
            <Controller
              name="city"
              control={control}
              render={({ field, fieldState }) => (
                <GoogleApiRestrictedInputField
                  id="add-address-form-city"
                  value={field.value}
                  onChange={(value: string) => field.onChange(value)}
                  placeholder={translator.translate(language, "shipping", "ShippingForm/location")}
                  mandatory={true}
                  country={cartCountryID}
                  onBlur={field.onBlur}
                  addressType={"(cities)"}
                  isGoogleApiLoaded={isGoogleAPILoaded}
                  restrictedChars={['@', '!']}
                  errorMsg={fieldState?.error?.message}
                />
              )}
            />
          </div>
        </div>

        {/* Street and house number */}
        <Controller
          name="address"
          control={control}
          render={({ field, fieldState }) => (
            <GoogleApiRestrictedInputField
              id="add-address-form-address"
              value={field.value}
              onChange={(value: any) => {
                field.onChange(Array.isArray(value.split(",")) ? value.split(",")[0] : value);
              }}
              placeholder={translator.translate(language, "shipping", "ShippingForm/street_and_house")}
              mandatory={true}
              country={cartCountryID}
              onBlur={field.onBlur}
              addressType={"address"}
              isGoogleApiLoaded={isGoogleAPILoaded}
              errorMsg={fieldState?.error?.message}
            />
          )}
        />

        {/* Country (locked) + Company */}
        <div className={`flex ${isMobile ? "flex-col gap-2" : "flex-row items-center gap-3"}`}>
          <div className={isMobile ? "w-full" : "flex-1"}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <Select
                  id="add-address-form-country"
                  initialValue={{
                    value: translator.translate(language, "shipping", `ShippingForm/country_name/${field.value?.toLowerCase()}`),
                    code: field.value,
                  }}
                  options={countryDropdownOptions as any}
                  onChangeCallback={() => {}}
                  questionnaireValidationObject={{}}
                  errorMode={false}
                  style={{
                    border: "1px solid var(--sub-gray)",
                    height: "56px",
                    width: "100%",
                    borderRadius: "6px",
                    margin: "0px",
                    backgroundColor: "#f7f7f7",
                  }}
                  label={translator.translate(language, "shipping", "ShippingForm/country")}
                  disabled={true}
                />
              )}
            />
          </div>
          <div className={isMobile ? "w-full" : "flex-1"}>
            <Controller
              name="company"
              control={control}
              render={({ field }) => (
                <RestrictedInputField
                  id="add-address-form-company"
                  ignoreTouched={true}
                  value={field.value}
                  onChange={(value: string) => field.onChange(value)}
                  placeholder={translator.translate(language, "shipping", "ShippingForm/firm_name")}
                  mandatory={false}
                  language={language}
                  onBlur={field.onBlur}
                />
              )}
            />
          </div>
        </div>

        {/* Phone */}
        <Controller
          name="phone"
          control={control}
          render={({ field, fieldState }) => {
            const handlePhoneBlur = () => {
              field.onBlur();
              const val = field.value;
              if (val && watch("country")?.toUpperCase() === GERMANY_COUNTRY_ID) {
                const result = validatePhoneDE(val, language);
                if (result.isValid && result.e164 && result.e164 !== val) {
                  setValue("phone", result.e164);
                }
              }
            };
            return (
              <div className="flex flex-col gap-2.5 w-full">
                {!isMobile && (
                  <div className="flex flex-row gap-4 items-start justify-center w-full">
                    <div className="flex flex-1 gap-3 items-center self-stretch">
                      <BsTelephone className="w-[25px] h-[25px] shrink-0 text-[#98a0af]" />
                      <p className="text-[13px] text-[#98a0af] font-poppins leading-[19.5px] m-0">
                        {translator.translate(language, "shipping", "ShippingForm/phone_icon_text")}
                      </p>
                    </div>
                    <div className="flex-1">
                      <RestrictedInputField
                        id="add-address-form-phone"
                        ignoreTouched={true}
                        value={field.value}
                        onChange={(value: string) => field.onChange(value)}
                        placeholder={translator.translate(language, "shipping", "ShippingForm/telephone")}
                        mandatory={true}
                        language={language}
                        onBlur={handlePhoneBlur}
                        errorMsg={fieldState?.error?.message}
                      />
                    </div>
                  </div>
                )}
                {isMobile && (
                  <>
                    <RestrictedInputField
                      id="add-address-form-phone"
                      ignoreTouched={true}
                      value={field.value}
                      onChange={(value: string) => field.onChange(value)}
                      placeholder={translator.translate(language, "shipping", "ShippingForm/telephone")}
                      mandatory={true}
                      language={language}
                      onBlur={handlePhoneBlur}
                      errorMsg={fieldState?.error?.message}
                    />
                    <div className="flex gap-3 items-center w-full">
                      <BsTelephone className="w-[25px] h-[25px] shrink-0 text-[#98a0af]" />
                      <p className="text-[13px] text-[#98a0af] font-poppins leading-[19.5px] m-0">
                        {translator.translate(language, "shipping", "ShippingForm/phone_icon_text")}
                      </p>
                    </div>
                  </>
                )}
              </div>
            );
          }}
        />

      </div>
    </div>
  );
};

export default AddressFormFields;
